import { Helmet } from 'react-helmet';
import { Link, useParams } from 'react-router-dom';
import { Check, ChevronRight, ShieldCheck } from 'lucide-react';
import { getProductBySlug } from '@/data/products';
import NotFound from '@/pages/NotFound';

const categoryCopy = {
  vpn: {
    title: 'No Subscription VPN Access',
    summary: 'Choose short term VPN access without creating a PhantomPath account or starting a recurring subscription.',
    detail: 'PhantomPath VPN passes are designed for people searching for a pay as you go VPN, temporary VPN, vacation VPN or VPN with no sign up. Choose the duration you need and receive access after payment.',
    useCases: ['Public Wi-Fi and shared networks', 'Short trips and holidays', 'One-off privacy sessions', 'People avoiding monthly VPN contracts'],
    faq: [
      ['Can I use this VPN without a subscription?', 'Yes. This is a one-off VPN pass with no automatic renewal.'],
      ['Do I need a PhantomPath account?', 'No. You do not need to create a PhantomPath account to use a VPN pass. Your chosen payment provider may process the information required to complete the transaction.'],
      ['Is this a disposable VPN?', 'It is better described as temporary or short-term VPN access. The pass expires after its stated duration without becoming a subscription.'],
      ['Can I use a short-term VPN for business travel?', 'Yes. Choose a duration that covers the work trip and use the VPN on compatible devices when connecting through hotel, airport or other shared networks.'],
      ['What does zero logs mean for a PhantomPath VPN pass?', 'PhantomPath does not retain browsing activity, DNS queries, VPN traffic content or communications content. Limited payment, provisioning, error, security and system records remain operationally separate.'],
      ['Does a VPN make me completely anonymous?', 'No. A VPN can protect traffic between your device and the VPN server and mask your IP address from destinations, but it does not remove cookies, account logins, device identifiers or information you provide to other services.'],
    ],
  },
  travel: {
    title: 'Travel eSIM Data',
    summary: 'Prepaid travel eSIM data for holidays, business trips and international travel, with no recurring subscription.',
    detail: 'Use mobile data abroad without adding another physical SIM. These travel eSIM plans are suitable for people comparing vacation eSIMs, international data packs and short-term travel connectivity.',
    useCases: ['International holidays', 'Business travel', 'Airport and hotel connectivity', 'Keeping a primary SIM installed while abroad'],
    faq: [
      ['Does the travel eSIM require a subscription?', 'No. Each eSIM pack is purchased as a one-off data package for its stated duration.'],
      ['Can I keep my physical SIM installed?', 'Yes. Compatible devices can use the travel eSIM alongside an existing physical SIM or eSIM.'],
      ['Where can I use the eSIM?', 'Select your destination from the supported-country list before choosing a package.'],
      ['Can a travel eSIM help reduce roaming charges?', 'Using the travel eSIM for mobile data can reduce reliance on your home provider\'s data roaming. Calls, texts, voicemail or accidental use by the primary SIM may still create charges.'],
      ['Is a travel eSIM the same as a VPN?', 'No. An eSIM provides mobile data. Choose a Privacy Pack or a separate VPN pass if you also need PhantomPath VPN access.'],
      ['Is this suitable for a business trip?', 'Yes. Choose a supported destination, data allowance and duration that match the trip. Check device compatibility before buying.'],
    ],
  },
  privacy: {
    title: 'Travel Privacy Pack',
    summary: 'Travel eSIM data bundled with short-term PhantomPath VPN access in one non-subscription package.',
    detail: 'Privacy Packs combine international mobile data and VPN access for travellers who want one package for connectivity and safer browsing on shared networks.',
    useCases: ['Travel data plus VPN access', 'Holidays and work trips', 'Public Wi-Fi protection', 'One-off privacy without a monthly plan'],
    faq: [
      ['What is included in a Privacy Pack?', 'A Privacy Pack includes the stated eSIM data allowance and PhantomPath VPN access for the selected duration.'],
      ['Is there a recurring payment?', 'No. Privacy Packs are one-off purchases with no automatic renewal.'],
      ['Can I add a virtual number?', 'An optional UK or USA virtual number can be selected where shown during package configuration.'],
      ['Is a Privacy Pack suitable for business travel?', 'Yes. It combines travel data with time-limited VPN access for the selected duration, which can suit work trips using hotel, airport or other shared networks.'],
      ['Can it help reduce mobile-data roaming charges?', 'Using the included eSIM for mobile data can reduce reliance on a home provider\'s data roaming. Charges may still apply if the primary SIM handles calls, texts or data.'],
      ['What privacy data does the VPN retain?', 'PhantomPath does not retain browsing activity, DNS queries or VPN traffic content. Payment providers keep their transaction records, while limited PhantomPath order records remain separate from VPN activity and communications content.'],
    ],
  },
  unlimited: {
    title: 'Unlimited Travel Data',
    summary: 'A 30-day unlimited data eSIM option for heavy data use, available with or without bundled VPN access.',
    detail: 'Designed for longer trips and higher data requirements, these contract-free plans avoid a recurring monthly subscription.',
    useCases: ['Longer international trips', 'Remote work while travelling', 'High mobile-data requirements', 'Data with optional VPN access'],
    faq: [
      ['How long does the unlimited pack last?', 'The unlimited pack is valid for 30 days.'],
      ['Does it renew automatically?', 'No. It is a one-off purchase and does not automatically renew.'],
      ['Can VPN access be included?', 'Choose the Unlimited Data + VPN package if you also require PhantomPath VPN access.'],
      ['Is unlimited travel data suitable for remote work?', 'It can suit heavier mobile-data requirements on a 30-day trip, subject to destination support, device compatibility, network coverage and the service terms.'],
      ['Can it help reduce reliance on home-network roaming?', 'Using the eSIM for mobile data can reduce reliance on a home provider\'s data roaming. The primary SIM may still create charges for calls, texts or accidental data use.'],
      ['Does every unlimited package include a VPN?', 'No. Only the package whose displayed name states that VPN is included contains PhantomPath VPN access.'],
    ],
  },
  numbers: {
    title: 'Temporary Virtual Number',
    summary: 'A dedicated UK or USA virtual number for 30 days that works alongside a compatible existing SIM or eSIM.',
    detail: 'For people comparing temporary, travel or disposable number services, PhantomPath provides a dedicated time-limited virtual number rather than another physical SIM. Platform verification compatibility and SMS delivery depend on third-party services and are not guaranteed.',
    useCases: ['Keeping a UK or USA number while travelling', 'Calls and messaging where supported', 'Separating temporary communications', 'Using a number alongside an existing SIM'],
    faq: [
      ['How long is the virtual number active?', 'The virtual-number package is provided for 30 days.'],
      ['Is this an emergency telephone service?', 'No. PhantomPath virtual numbers do not support emergency calling, including 999, 112 or 911.'],
      ['Will every verification service accept the number?', 'No. Compatibility depends on each third-party platform and cannot be guaranteed.'],
      ['Can I use this as a second number for business travel?', 'It can provide a separate UK or USA number for supported general calls and messaging during a 30-day trip. It is not a permanent business telephone system.'],
      ['Does a virtual number make me anonymous?', 'No. It can keep a personal number separate from supported temporary communications, but payment, telecom compliance and provider processing still apply.'],
      ['Do I need another physical SIM?', 'No. The virtual number works over a suitable internet connection alongside a compatible existing SIM or eSIM.'],
    ],
  },
};

const relatedGuides = {
  vpn: [
    ['/services/no-account-vpn', 'VPN access without a traditional account'],
    ['/compare/pay-as-you-go-vpn-vs-subscription', 'Pay as you go VPN versus subscription'],
    ['/learn/privacy-architecture', 'Technical privacy architecture'],
  ],
  travel: [
    ['/guides/avoid-roaming-charges-with-esim', 'How to reduce roaming charges with an eSIM'],
    ['/services/holiday-esim-data', 'Holiday eSIM data and VPN'],
    ['/services/business-travel-connectivity', 'Connectivity for business travellers'],
  ],
  privacy: [
    ['/learn/temporary-identity-systems', 'How eSIM + VPN Packs work'],
    ['/services/business-travel-connectivity', 'Connectivity for business travellers'],
    ['/learn/privacy-architecture', 'Technical privacy architecture'],
  ],
  unlimited: [
    ['/services/business-travel-connectivity', 'Connectivity for business travellers'],
    ['/guides/avoid-roaming-charges-with-esim', 'How to reduce roaming charges with an eSIM'],
    ['/learn/privacy-architecture', 'Technical privacy architecture'],
  ],
  numbers: [
    ['/services/business-virtual-number', 'Temporary numbers for business travel'],
    ['/services/virtual-numbers', 'UK and USA virtual-number guide'],
    ['/learn/privacy-architecture', 'How communications data is separated'],
  ],
};

const getBuyerAngles = (product) => {
  const hasVpn = product.category === 'vpn' || product.category === 'privacy' || product.features.some((feature) => feature.toLowerCase().includes('vpn access'));
  const dataAmount = product.data || (product.category === 'unlimited' ? 'unlimited data' : null);

  if (product.category === 'vpn') return [
    ['Business travel', `The ${product.duration} access period can cover a defined work trip or temporary use on hotel, airport and other shared networks without starting a recurring VPN plan.`],
    ['Holidays and short trips', `Choose the ${product.duration} pass when the trip or temporary requirement has a clear end date. There is no automatic renewal.`],
    ['Technical privacy', 'No PhantomPath account is required. VPN traffic uses RAM-only routing, and PhantomPath does not retain browsing activity, DNS queries or VPN traffic content.'],
  ];

  if (product.category === 'travel') return [
    ['Business travel', `${dataAmount} for ${product.duration} can provide mobile data for a supported work destination without a recurring travel-data contract.`],
    ['Holidays and roaming', 'Selecting the travel eSIM for mobile data can reduce reliance on home-network data roaming. Primary-SIM calls, texts or accidental data use may still create charges.'],
    ['Technical privacy', 'An eSIM provides connectivity, not anonymity. This package does not include VPN access; choose an eSIM + VPN Pack or separate VPN pass if that protection is also required.'],
  ];

  if (product.category === 'privacy') return [
    ['Business travel', `${dataAmount} of travel eSIM data and VPN access are combined for the same ${product.duration} period, reducing the need to arrange two separate services.`],
    ['Holidays and roaming', 'The travel eSIM can reduce reliance on home-network data roaming, while the included VPN supports safer browsing on compatible devices and shared networks.'],
    ['Technical privacy', 'VPN traffic uses RAM-only routing with no retained browsing, DNS-query or VPN-traffic logs. Payment and limited order records remain operationally separate from VPN activity.'],
  ];

  if (product.category === 'unlimited') return [
    ['Business and remote work', `The 30-day ${dataAmount} package can suit heavier travel-data requirements, subject to destination support, coverage, device compatibility and service terms.`],
    ['Longer holidays', 'A single 30-day travel-data package can reduce the need to monitor a fixed data allowance, while careful primary-SIM settings help avoid accidental roaming use.'],
    ['Technical privacy', hasVpn ? 'This displayed package includes PhantomPath VPN access with RAM-only routing and no retained browsing, DNS-query or VPN-traffic logs.' : 'This displayed package provides travel data and does not include VPN access. Choose the package whose name states that VPN is included if you need both services.'],
  ];

  return [
    ['Business travel', `A dedicated ${product.name.startsWith('UK') ? 'UK' : 'USA'} number can keep supported work calls and messages separate from a personal number during its 30-day period.`],
    ['Holidays and temporary contact', 'The number works over a suitable data connection without another physical SIM. Calling and messaging depend on the options selected and third-party telecom networks.'],
    ['Technical privacy', 'A separate number can reduce how often a personal number is shared, but it does not create anonymity. Payment, telecom-compliance and provider processing still apply.'],
  ];
};

const trimMetaDescription = (value, limit = 155) => {
  if (value.length <= limit) return value;
  const shortened = value.slice(0, limit + 1);
  return `${shortened.slice(0, shortened.lastIndexOf(' ')).replace(/[,.]$/, '')}.`;
};

const categoryAnchors = {
  vpn: 'pricing',
  travel: 'travel-packs',
  privacy: 'privacy-packs',
  unlimited: 'unlimited-packs',
  numbers: 'virtual-numbers',
};

const ServicePage = () => {
  const { slug } = useParams();
  const product = getProductBySlug(slug);
  if (!product) return <NotFound />;

  const copy = categoryCopy[product.category];
  const buyerAngles = getBuyerAngles(product);
  const canonical = `https://phantompathvpn.com/services/${product.slug}`;
  const description = trimMetaDescription(`${product.description} ${copy.summary}`);
  const structuredData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Product',
        name: product.name,
        image: 'https://phantompathvpn.com/phantompath-digital-service-product.png',
        description,
        category: product.categoryLabel,
        brand: { '@type': 'Brand', name: 'PhantomPathVPN' },
        offers: {
          '@type': 'Offer',
          url: canonical,
          priceCurrency: 'GBP',
          price: product.amount.toFixed(2),
          availability: 'https://schema.org/InStock',
          seller: { '@type': 'Organization', name: 'PhantomPathVPN Ltd' },
        },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://phantompathvpn.com/' },
          { '@type': 'ListItem', position: 2, name: product.categoryLabel, item: `https://phantompathvpn.com/#${categoryAnchors[product.category]}` },
          { '@type': 'ListItem', position: 3, name: product.name, item: canonical },
        ],
      },
      {
        '@type': 'FAQPage',
        mainEntity: copy.faq.map(([question, answer]) => ({
          '@type': 'Question',
          name: question,
          acceptedAnswer: { '@type': 'Answer', text: answer },
        })),
      },
    ],
  };

  return (
    <main className="min-h-screen bg-[#050505] px-4 pb-20 pt-28 text-white">
      <Helmet>
        <title>{`${product.name} | PhantomPathVPN`}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonical} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={`${product.name} | PhantomPathVPN`} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonical} />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      <div className="mx-auto max-w-5xl">
        <nav aria-label="Breadcrumb" className="mb-8 flex flex-wrap items-center gap-2 text-xs text-gray-400">
          <Link to="/" className="hover:text-white">Home</Link><ChevronRight className="h-3 w-3" />
          <Link to={`/#${product.slug}`} className="hover:text-white">{product.categoryLabel}</Link><ChevronRight className="h-3 w-3" />
          <span className="text-gray-200">{product.name}</span>
        </nav>

        <section className="rounded-3xl border border-white/10 bg-white/[0.035] p-6 sm:p-10">
          <p className="mb-3 text-xs font-bold uppercase tracking-[0.22em]" style={{ color: product.accent }}>{product.eyebrow}</p>
          <h1 className="max-w-4xl text-3xl font-extrabold leading-tight sm:text-5xl">{product.name}</h1>
          <p className="mt-4 max-w-3xl text-base leading-7 text-gray-300 sm:text-lg">{copy.summary}</p>

          <div className="mt-8 flex flex-wrap items-end gap-5 border-y border-white/10 py-6">
            <div><span className="block text-xs uppercase tracking-wider text-gray-500">Price</span><strong className="text-4xl">{product.price}</strong></div>
            <div><span className="block text-xs uppercase tracking-wider text-gray-500">Duration</span><strong className="text-lg">{product.duration}</strong></div>
            <Link to={`/#${product.slug}`} className="ml-auto rounded-xl px-6 py-3 text-sm font-bold text-[#03110d] transition hover:brightness-110" style={{ backgroundColor: product.accent }}>View package options</Link>
          </div>

          <div className="mt-8 grid gap-8 md:grid-cols-2">
            <div>
              <h2 className="text-xl font-bold">What this package provides</h2>
              <ul className="mt-4 space-y-3">{product.features.map((feature) => <li key={feature} className="flex gap-3 text-sm leading-6 text-gray-300"><Check className="mt-1 h-4 w-4 shrink-0" style={{ color: product.accent }} />{feature}</li>)}</ul>
            </div>
            <div>
              <h2 className="text-xl font-bold">Designed for</h2>
              <ul className="mt-4 space-y-3">{copy.useCases.map((useCase) => <li key={useCase} className="flex gap-3 text-sm leading-6 text-gray-300"><ShieldCheck className="mt-1 h-4 w-4 shrink-0" style={{ color: product.accent }} />{useCase}</li>)}</ul>
            </div>
          </div>
        </section>

        <section className="mt-10" aria-labelledby="buyer-contexts">
          <h2 id="buyer-contexts" className="text-2xl font-bold">Where this package fits</h2>
          <div className="mt-5 grid gap-4 md:grid-cols-3">
            {buyerAngles.map(([title, detail]) => (
              <article key={title} className="rounded-2xl border border-white/10 bg-white/[0.035] p-5">
                <h2 className="font-bold" style={{ color: product.accent }}>{title}</h2>
                <p className="mt-3 text-sm leading-7 text-gray-300">{detail}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="mx-auto mt-10 max-w-4xl">
          <h2 className="text-2xl font-bold">{copy.title}, explained</h2>
          <p className="mt-4 leading-8 text-gray-300">{copy.detail}</p>
          <div className="mt-8 divide-y divide-white/10 border-y border-white/10">
            {copy.faq.map(([question, answer]) => <article key={question} className="py-5"><h3 className="font-bold text-white">{question}</h3><p className="mt-2 text-sm leading-6 text-gray-400">{answer}</p></article>)}
          </div>
        </section>

        <nav className="mx-auto mt-12 max-w-4xl border-t border-white/10 pt-8" aria-label="Related buying guides">
          <h2 className="text-lg font-bold text-white">Related buying guides</h2>
          <div className="mt-4 flex flex-wrap gap-3">
            {relatedGuides[product.category].map(([target, label]) => (
              <Link key={target} to={target} className="inline-flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.035] px-4 py-3 text-sm font-semibold text-gray-200 transition hover:border-white/25 hover:text-white">
                {label}<ChevronRight className="h-4 w-4" />
              </Link>
            ))}
          </div>
        </nav>
      </div>
    </main>
  );
};

export default ServicePage;
